import apiService from './apiService';

// TypeScript interfaces
export type DepreciationMethod =
  | 'STRAIGHT_LINE'
  | 'DECLINING_BALANCE'
  | 'SUM_OF_YEARS'
  | 'DECLINING_THEN_STRAIGHT_LINE';

export type AssetState = 'DRAFT' | 'RUNNING' | 'DISPOSED' | 'FULLY_DEPRECIATED';

export type DisposalType = 'SALE' | 'SCRAP' | 'DONATION' | 'LOSS';

export interface AssetCategory {
  id: string;
  name: string;
  code: string;
  description?: string;
  depreciationMethod: DepreciationMethod;
  durationMonths: number; // Odoo: Duration in months
  accountAssetId?: string;
  accountDepreciationId?: string;
  accountExpenseId?: string;
  accountGainLossId?: string;
  journalId?: string;
  automateAssets?: 'NO' | 'CREATE_DRAFT' | 'CREATE_VALIDATE';
  prorataTemporis?: boolean;
  companyId: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  journal?: {
    id: string;
    name: string;
    code: string;
  };
}

export interface Asset {
  id: string;
  name: string;
  code: string;
  categoryId: string;
  description?: string;
  serialNumber?: string;
  location?: string;
  acquisitionDate: string; // Odoo: Acquisition Date
  originalValue: number; // Odoo: Original Value
  notDepreciableValue: number; // Odoo: Not Depreciable Value
  depreciableValue: number; // Odoo: Depreciable Value (calculated)
  duration: number; // Odoo: Duration in months
  depreciationMethod: DepreciationMethod;
  accumulatedDepreciation: number;
  bookValue: number;
  state: AssetState;
  vendorId?: string;
  purchaseInvoiceId?: string;
  journalEntryId?: string;
  analyticAccountId?: string;
  prorataTemporis: boolean; // Odoo: Prorata Temporis checkbox
  startDepreciatingDate?: string; // Odoo: Start Depreciating date
  firstDepreciationDate?: string;
  lastDepreciationDate?: string;
  nextDepreciationDate?: string;
  declineFactor?: number;
  companyId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  category?: AssetCategory;
  depreciations?: AssetDepreciation[];
  disposal?: AssetDisposal;
}

export interface AssetDepreciation {
  id: string;
  assetId: string;
  depreciationDate: string;
  amount: number;
  accumulatedAmount: number;
  bookValue: number;
  remainingValue: number;
  sequence: number;
  journalEntryId?: string;
  state: 'DRAFT' | 'POSTED';
  postedAt?: string;
  postedBy?: string;
  createdAt: string;
}

export interface AssetDisposal {
  id: string;
  assetId: string;
  disposalDate: string;
  disposalType: DisposalType;
  disposalValue: number;
  gainLoss: number;
  journalEntryId?: string;
  invoiceId?: string;
  customerId?: string;
  notes?: string;
  createdBy?: string;
  createdAt: string;
}

export interface CreateAssetCategoryInput {
  name: string;
  code: string;
  description?: string;
  depreciationMethod: DepreciationMethod;
  durationMonths: number; // Odoo: Duration in months
  accountAssetId?: string;
  accountDepreciationId?: string;
  accountExpenseId?: string;
  accountGainLossId?: string;
  journalId?: string;
  automateAssets?: 'NO' | 'CREATE_DRAFT' | 'CREATE_VALIDATE';
  prorataTemporis?: boolean;
}

export interface CreateAssetInput {
  name: string;
  code: string;
  categoryId: string;
  description?: string;
  serialNumber?: string;
  location?: string;
  acquisitionDate: string; // Odoo: Acquisition Date
  originalValue: number; // Odoo: Original Value
  notDepreciableValue?: number; // Odoo: Not Depreciable Value
  duration: number; // Odoo: Duration in months
  depreciationMethod?: DepreciationMethod;
  declineFactor?: number;
  prorataTemporis?: boolean; // Odoo: Prorata Temporis checkbox
  startDepreciatingDate?: string; // Odoo: Start Depreciating date
  firstDepreciationDate?: string;
  vendorId?: string;
  purchaseInvoiceId?: string;
  analyticAccountId?: string;
}

export interface UpdateAssetInput {
  name?: string;
  description?: string;
  location?: string;
  analyticAccountId?: string;
  // Only for DRAFT assets:
  categoryId?: string;
  serialNumber?: string;
  purchaseDate?: string;
  purchaseValue?: number;
  salvageValue?: number;
  usefulLife?: number;
  depreciationMethod?: DepreciationMethod;
  declineFactor?: number;
  prorataDate?: string;
  firstDepreciationDate?: string;
}

export interface DisposeAssetInput {
  disposalDate: string;
  disposalType: DisposalType;
  disposalValue?: number;
  notes?: string;
  invoiceId?: string;
  customerId?: string;
}

export interface SellAssetInput {
  saleValue: number;
  saleDate: string;
  customerId: string;
  notes?: string;
}

export interface ReevaluateAssetInput {
  newOriginalValue?: number; // Odoo: Original Value
  newNotDepreciableValue?: number; // Odoo: Not Depreciable Value
  newDuration?: number; // Odoo: Duration in months
  reason: string;
}

export interface DepreciationSchedule {
  assetId: string;
  assetCode: string;
  assetName: string;
  originalValue: number; // Odoo: Original Value
  notDepreciableValue: number; // Odoo: Not Depreciable Value
  depreciableValue: number; // Odoo: Depreciable Value
  duration: number; // Odoo: Duration in months
  depreciationMethod: string;
  schedule: {
    sequence: number;
    period: number;
    date: string;
    amount: number;
    accumulatedAmount: number;
    bookValue: number;
    remainingValue: number;
    posted: boolean;
    journalEntryId?: string;
  }[];
}

export interface BatchDepreciationOptions {
  assetIds?: string[];
  upToDate: string;
  previewOnly?: boolean;
}

export interface BatchDepreciationResult {
  preview: {
    assetId: string;
    assetCode: string;
    assetName: string;
    date: string;
    amount: number;
    sequence: number;
  }[];
  posted?: number;
  journalEntries?: string[];
}

export interface PendingDepreciation {
  assetId: string;
  assetCode: string;
  assetName: string;
  categoryName: string;
  pendingCount: number;
  pendingAmount: number;
  nextDepreciationDate: string;
}

export interface AssetSummary {
  totalAssets: number;
  totalPurchaseValue: number;
  totalBookValue: number;
  totalAccumulatedDepreciation: number;
  byState: { state: string; count: number; bookValue: number }[];
  byCategory: { categoryId: string; categoryName: string; count: number; bookValue: number }[];
}

export interface AssetRegister {
  assets: Asset[];
  summary: {
    totalAssets: number;
    totalPurchaseValue: number;
    totalAccumulatedDepreciation: number;
    totalBookValue: number;
    byCategory: {
      categoryId: string;
      categoryName: string;
      count: number;
      purchaseValue: number;
      accumulatedDepreciation: number;
      bookValue: number;
    }[];
  };
}

export interface DepreciationSummary {
  totalDepreciation: number;
  byCategory: {
    categoryId: string;
    categoryName: string;
    depreciation: number;
  }[];
  byAsset: {
    assetId: string;
    assetCode: string;
    assetName: string;
    categoryName: string;
    depreciation: number;
  }[];
}

export interface AssetFilters {
  state?: AssetState;
  categoryId?: string;
}

// Asset Service class
class AssetService {
  private baseUrl = '/accounting';

  // ==================== Category Methods ====================

  async getCategories(): Promise<AssetCategory[]> {
    const response = await apiService.get(`${this.baseUrl}/asset-categories`);
    return response.data || [];
  }

  async createCategory(data: CreateAssetCategoryInput): Promise<AssetCategory> {
    const response = await apiService.post(`${this.baseUrl}/asset-categories`, data);
    return response.data;
  }

  async updateCategory(id: string, data: Partial<CreateAssetCategoryInput>): Promise<AssetCategory> {
    const response = await apiService.put(`${this.baseUrl}/asset-categories/${id}`, data);
    return response.data;
  }

  async deleteCategory(id: string): Promise<void> {
    await apiService.delete(`${this.baseUrl}/asset-categories/${id}`);
  }

  // ==================== Asset Methods ====================

  async getAssets(filters?: AssetFilters): Promise<Asset[]> {
    const params = new URLSearchParams();
    if (filters?.state) params.append('state', filters.state);
    if (filters?.categoryId) params.append('categoryId', filters.categoryId);

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiService.get(`${this.baseUrl}/assets${query}`);
    return response.data || [];
  }

  async getAsset(id: string): Promise<Asset> {
    const response = await apiService.get(`${this.baseUrl}/assets/${id}`);
    return response.data;
  }

  async createAsset(data: CreateAssetInput): Promise<Asset> {
    const response = await apiService.post(`${this.baseUrl}/assets`, data);
    return response.data;
  }

  async updateAsset(id: string, data: UpdateAssetInput): Promise<Asset> {
    const response = await apiService.put(`${this.baseUrl}/assets/${id}`, data);
    return response.data;
  }

  async deleteAsset(id: string): Promise<void> {
    await apiService.delete(`${this.baseUrl}/assets/${id}`);
  }

  async confirmAsset(id: string): Promise<Asset> {
    const response = await apiService.post(`${this.baseUrl}/assets/${id}/confirm`);
    return response.data;
  }

  // ==================== Depreciation Methods ====================

  async getDepreciationSchedule(assetId: string): Promise<DepreciationSchedule> {
    const response = await apiService.get(`${this.baseUrl}/assets/${assetId}/depreciation-schedule`);
    return response.data;
  }

  async postDepreciation(assetId: string, depreciationDate: string): Promise<AssetDepreciation> {
    const response = await apiService.post(`${this.baseUrl}/assets/${assetId}/depreciate`, {
      depreciationDate
    });
    return response.data;
  }

  async computeBatchDepreciation(options: BatchDepreciationOptions): Promise<BatchDepreciationResult> {
    const response = await apiService.post(`${this.baseUrl}/assets/compute-depreciation`, options);
    return response.data;
  }

  async getPendingDepreciations(upToDate?: string): Promise<PendingDepreciation[]> {
    const params = upToDate ? `?upToDate=${upToDate}` : '';
    const response = await apiService.get(`${this.baseUrl}/assets/pending-depreciation${params}`);
    return response.data || [];
  }

  // ==================== Disposal Methods ====================

  async disposeAsset(assetId: string, data: DisposeAssetInput): Promise<AssetDisposal> {
    const response = await apiService.post(`${this.baseUrl}/assets/${assetId}/dispose`, data);
    return response.data;
  }

  async undoDisposal(assetId: string): Promise<Asset> {
    const response = await apiService.post(`${this.baseUrl}/assets/${assetId}/undo-disposal`);
    return response.data;
  }

  async sellAsset(assetId: string, data: SellAssetInput): Promise<{ asset: Asset; disposal: AssetDisposal }> {
    const response = await apiService.post(`${this.baseUrl}/assets/${assetId}/sell`, data);
    return response.data;
  }

  // ==================== Reevaluation Methods ====================

  async reevaluateAsset(assetId: string, data: ReevaluateAssetInput): Promise<Asset> {
    const response = await apiService.post(`${this.baseUrl}/assets/${assetId}/reevaluate`, data);
    return response.data;
  }

  // ==================== Reports Methods ====================

  async getAssetRegister(filters?: {
    categoryId?: string;
    state?: string;
    asOfDate?: string;
  }): Promise<AssetRegister> {
    const params = new URLSearchParams();
    if (filters?.categoryId) params.append('categoryId', filters.categoryId);
    if (filters?.state) params.append('state', filters.state);
    if (filters?.asOfDate) params.append('asOfDate', filters.asOfDate);

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiService.get(`${this.baseUrl}/assets/register${query}`);
    return response.data;
  }

  async getDepreciationSummary(fromDate: string, toDate: string): Promise<DepreciationSummary> {
    const response = await apiService.get(
      `${this.baseUrl}/assets/depreciation-summary?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response.data;
  }

  async getAssetSummary(): Promise<AssetSummary> {
    const response = await apiService.get(`${this.baseUrl}/assets/summary`);
    return response.data;
  }
}

export default new AssetService();
